import { gtmEmail, localStorage } from "@jog/shared"
import { AdpageState } from "@jog/types"
import { useSession } from "next-auth/react"
import { useRouter } from "next/router"
import Script from "next/script"
import { PropsWithChildren, createContext, memo, useContext, useEffect, useState } from "react"

declare global {
    interface Window {
        dataLayer: Record<string, any>[]
        taggingHelpers?: {
            getDeviceInfo?: () => AdpageState["device"]
            getMarketingObject?: () => AdpageState["marketing"]
        }
    }
}
const AdpageContext = createContext<AdpageState>({ device: null, marketing: null })

export const useAdpage = () => useContext(AdpageContext)!
const useGtmInit = () => {
    const session = useSession()
    useEffect(() => {
        const localEmail = localStorage.getItem<string>("email")
        const email = session?.data?.user?.email || localEmail
        if (email) {
            gtmEmail(email)
            localStorage.setItem<string>("email", email)
        }
    }, [session?.data?.user?.email])
}

export const GtmStagingEnv = () => {
    useGtmInit()
    return (
        <Script id="google-tag-manager" defer>
            {`
            function push(...params) {if (!params) return;params.forEach((i) => {console.log(i);i.__t = new Date();});window.dataLayer = [...window.dataLayer, ...params];window.dataLayer.push = push;}
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push = push
            `}
        </Script>
    )
}

export const getAdpage = () => {
    const tagging = process.env.NEXT_PUBLIC_ADPAGE_TAGGING
    const gtm = process.env.NEXT_PUBLIC_GTM
    return { tagging, gtm }
}
export const AdpageHead = memo(() => {
    const { tagging, gtm } = getAdpage()
    useGtmInit()
    // if (process.env.NODE_ENV !== "production") return <></>
    if (tagging && gtm)
        return (
            <Script defer id="adpage-head" strategy="afterInteractive">{`(function(){function t(t, e, r) {
if ("cookie" === t) {
var n = document.cookie.split(";");
for (var o = 0; o < n.length; o++) {
var i = n[o].split("=");
if (i[0].trim() === e) return i[1]
}
} else if ("localStorage" === t) return localStorage.getItem(e); else if ("jsVariable" === t) return window[e]; else console.warn("invalid uid source", t)
}function e(e,r,n){var o=document.createElement("script");o.async=!0,o.src=r,e.insertBefore(o,n)}function r(r,n,o,i,a){var c,s=!1;try{var u=navigator.userAgent,f=/Version\\/([0-9\\._]+)(.*Mobile)?.*Safari.*/.exec(u);f&&parseFloat(f[1])>=16.4&&(c=t(o,i,""),s=!0)}catch(t){console.error(t)}var l=window[a]=window[a]||[];l.push({"gtm.start":(new Date).getTime(),event:"gtm.js"});var g=r+"/script.js?id="+n+(s?"&enableCK=true":"")+(c?"&mcookie="+encodeURIComponent(c):""),d=document.getElementsByTagName("script")[0];e(d.parentNode,g,d)}r('${tagging}',"${gtm}","cookie","_taggingmk","dataLayer")})();
`}</Script>
        )
    return <></>
})
AdpageHead.displayName = "AdpageHead"
export const AdpageBody = memo(({ children }: PropsWithChildren) => {
    const [adpageState, setAdpageState] = useState<AdpageState>({ marketing: null, device: null })
    const { tagging, gtm } = getAdpage()
    return (
        <>
            <GtmScript />
            <AdpageContext.Provider value={adpageState}>
                {children}
                {tagging && gtm ? (
                    <>
                        <noscript>
                            <iframe src={`${tagging}/ns.html?id=${gtm}`} height="0" width="0" className="hidden" />
                        </noscript>
                        <Script
                            onLoad={() => {
                                const device = window.taggingHelpers?.getDeviceInfo?.()
                                const marketing = window.taggingHelpers?.getMarketingObject?.()
                                if (device && marketing) {
                                    setAdpageState({ device, marketing })
                                }
                            }}
                            src={`${tagging}/user-data-minified.js`}
                        />
                        <Script defer src={`${tagging}/adex.js`}></Script>
                        <Script defer src={`${tagging}/settings.js`}></Script>
                    </>
                ) : (
                    <></>
                )}
            </AdpageContext.Provider>
        </>
    )
})
AdpageBody.displayName = "AdpageBody"

export const GtmScript = () => {
    const router = useRouter()
    const [enable, setEnable] = useState(false)
    useEffect(() => {
        if (router.isReady) {
            setEnable(!("gtm-disable" in router.query))
        }
    }, [router.isReady, router.query])
    if (!enable) return <></>
    return process.env.NEXT_PUBLIC_GTM_ENABLE && process.env.NODE_ENV === "production" ? (
        <AdpageHead />
    ) : (
        <GtmStagingEnv />
    )
}
